import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, BookOpen, Globe, Feather } from 'lucide-react';
import { Language } from '../App';
interface AboutSectionProps {
  language: Language;
}
export function AboutSection({
  language
}: AboutSectionProps) {
  const isPashto = language === 'pashto';
  const pillars = isPashto ? [{
    icon: BookOpen,
    title: 'لیکلې پښتو ټولو ته ورسوو',
    description: 'د پښتو لیکلو او لوستلو وړتیا هر پښتون ته ورسول.'
  }, {
    icon: Globe,
    title: 'ادبي اثار خوندي او خپاره کړو',
    description: 'د پښتو شاعرۍ، ادب او علمي لیکنو ساتنه او خپرول.'
  }, {
    icon: Feather,
    title: 'راتلونکو نسلونو ته د ژبې مینه وسپارو',
    description: 'د ژبې مینه او پیژندنه نویو نسلونو ته لېږدول.'
  }] : [{
    icon: BookOpen,
    title: 'Bring Written Pashto to All',
    description: 'Making Pashto literacy accessible to every Pashtun, everywhere.'
  }, {
    icon: Globe,
    title: 'Preserve & Share Literary Works',
    description: 'Safeguarding Pashto poetry, literature, and scholarly writing for the world.'
  }, {
    icon: Feather,
    title: 'Pass the Love of Language Forward',
    description: 'Inspiring future generations to cherish and carry forward their mother tongue.'
  }];
  return <section className="relative py-8 md:py-12 px-6 overflow-hidden text-start" data-id="element-31">
      {/* Soft background tint */}
      <div className="absolute inset-0 bg-gradient-to-b from-pasoon-green/40 via-white to-white pointer-events-none" data-id="element-32"></div>
      <div className={`absolute top-0 w-[400px] h-[400px] bg-pasoon-green/30 rounded-full blur-[120px] pointer-events-none -translate-y-1/2 ${isPashto ? 'left-0 -translate-x-1/4' : 'right-0 translate-x-1/4'}`} data-id="element-33"></div>

      <div className="relative z-10 max-w-3xl mx-auto" data-id="element-34">
        <AnimatePresence mode="wait" data-id="element-35">
          <motion.div key={language} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-36">
            {/* Heading */}
            <div className="mb-8" data-id="element-37">
              <h2 className={`text-3xl md:text-5xl font-bold text-pasoon-dark inline-block relative ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-38">
                {isPashto ? 'پاڅون — د پښتو د بقا او ودې غږ' : 'Pasoon — The Voice of Pashto Survival & Growth'}
                <span className="absolute -bottom-3 start-0 w-1/3 h-1 bg-pasoon-red rounded-full" data-id="element-39"></span>
              </h2>
            </div>

            {/* Summary */}
            <p className={`text-lg md:text-xl text-gray-600 mb-8 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-40">
              {isPashto ? 'پښتو، چې د خیبر پښتونخوا، بلوچستان او افغانستان ترڅنګ د نړۍ په ګڼو سیمو کې ویل کېږي، د میلیونونو خلکو مورنۍ ژبه ده—خو ډېری یې لا هم د لوستلو او لیکلو له اسانتیاوو بې‌برخې دي.' : 'Pashto, spoken across Khyber Pakhtunkhwa, Balochistan, Afghanistan, and many regions around the world, is the mother tongue of millions — yet many still lack access to reading and writing it with ease.'}
            </p>

            {/* The Big Statement */}
            <blockquote className="relative my-10 md:my-12" data-id="element-41">
              <div className="absolute top-0 bottom-0 w-1 bg-pasoon-red rounded-full start-[-1rem] md:start-[-2rem]" data-id="element-42"></div>
              <p className={`text-2xl md:text-4xl font-bold text-pasoon-dark ${isPashto ? 'font-pashto leading-loose' : 'font-serif leading-snug md:leading-snug'}`} data-id="element-43">
                {isPashto ? 'موږ باور لرو چې ژبه یوازې په خبرو نه ژوندي پاتې کېږي، ' : 'We believe a language does not survive on speech alone — '}
                <span className="text-pasoon-red" data-id="element-44">
                  {isPashto ? 'بلکې په لیک، ادب او زده‌کړه کې وده کوي.' : 'it grows through writing, literature, and education.'}
                </span>
              </p>
              <p className={`mt-6 text-lg md:text-xl text-gray-600 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-45">
                {isPashto ? 'پاڅون همدې تشې ته ځواب دی.' : 'Pasoon is the answer to this gap.'}
              </p>
            </blockquote>

            {/* Our Goals heading */}
            <h3 className={`text-xl md:text-2xl font-bold text-pasoon-dark mb-6 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-46">
              {isPashto ? 'زموږ موخه دا ده چې:' : 'Our Mission:'}
            </h3>

            {/* Mission Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-47">
              {pillars.map((pillar, idx) => <div key={pillar.title} className="text-start" data-id="element-48">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-pasoon-red/10 mb-4" data-id="element-49">
                    <pillar.icon className="w-5 h-5 text-pasoon-red" data-id="element-50" />
                  </div>
                  <h3 className={`text-lg font-bold text-pasoon-dark mb-2 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-51">
                    {pillar.title}
                  </h3>
                  <p className={`text-gray-500 text-sm md:text-base ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-52">
                    {pillar.description}
                  </p>
                </div>)}
            </div>

            {/* Closing Statement */}
            <div className="mt-10 md:mt-12 p-6 md:p-8 bg-pasoon-dark rounded-2xl" data-id="element-53">
              <p className={`text-xl md:text-2xl font-bold text-white text-center ${isPashto ? 'font-pashto leading-loose' : 'font-serif'}`} data-id="element-54">
                {isPashto ? 'پاڅون یوازې یوه مجله نه ده—دا د یو ژوندي تمدن ساتونکی غږ دی' : 'Pasoon is not just a magazine — it is the guardian voice of a living civilization.'}
              </p>
            </div>

            {/* Support CTA */}
            <div className="mt-10 md:mt-12 text-center md:text-start" data-id="element-55">
              <button className={`inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-pasoon-red text-white rounded-full font-medium text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-56">
                <Heart className="w-5 h-5" data-id="element-57" />
                {isPashto ? 'د ماموریت سره مرسته وکړئ' : 'Support the Mission'}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}